<template>
  <el-container class="background-white" v-model="loading">
    <el-header>
      <h1>Pending Refunds</h1>
    </el-header>
    <el-row type="flex" justify="center">
      <el-col :span="23">
        <div class="grid-content bg-purple-light">
          <el-card v-loading="loading" class="box-card">
            <el-form label-position="left" :inline="true" :model="refundSearchForm" ref="refundSearchForm">
              <el-form-item label="Order ID" prop="order_id" >
                <el-input v-model="refundSearchForm.order_id" placeholder="Enter ID...">
                </el-input>
              </el-form-item>
              <el-form-item label="Type" prop="filter.return">
                <el-select v-model="refundSearchForm.filter.return" style="width: 200px">
                  <el-option
                      v-for="item in returnOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button :loading="loading" type="primary" @click="fetchRefunds">Submit</el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </div>
      </el-col>
    </el-row>
    <el-main>
      <el-row>
        <el-col :span="24">
          <el-table
              v-loading="loading"
              :data="refunds"
              style="width: 100%"
              max-height="600">
            <el-table-column
                label="Order ID"
            >
              <template slot-scope="scope">
                <a :href="scope.row.link" target="_blank" style="display: block;">{{ scope.row.order_id }}</a>
              </template>
            </el-table-column>
            <el-table-column
                prop="region"
                label="Store"
            >
            </el-table-column>
            <el-table-column
                prop="created_at"
                label="Date Created"
            >
            </el-table-column>
            <el-table-column
                label="LogON RA Amount">
              <template slot-scope="scope">
                <span v-if="scope.row.logon_amount != null">${{ parseFloat(scope.row.logon_amount).toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column
                label="Transactions"
                :min-width="150">
              <template slot-scope="scope">
                <li v-for="(trx, index) in scope.row.payload.transactions" :key="scope.row.order_id + index">
                  ${{ parseFloat(trx.amount).toFixed(2) }}{{ trx.currency }} {{ trx.kind }} <span v-if="trx.kind !== 'adjustment'"> | {{ trx.gateway }}</span>
                </li>
              </template>
            </el-table-column>
            <el-table-column
                align="right">
              <template slot-scope="scope">
                <span v-if="scope.row.logon_amount != null && scope.row.logon_amount.toFixed(2) != scope.row.refund_amount.toFixed(2)">
                  <el-button
                   :disabled="loading"
                   size="mini"
                   @click="warnDifference(scope.row)"
                  >
                    Process
                  </el-button>
                </span>
                <span v-else>
                  <el-popconfirm
                      :title="`Are you sure?`"
                      confirmButtonText="Confirm"
                      cancelButtonText="Cancel"
                      icon="el-icon-info"
                      iconColor="green"
                      @onConfirm="process(scope.row.id)">
                    <el-button :disabled="loading" slot="reference" size="mini">Process</el-button>
                  </el-popconfirm>
                </span>
                <el-popconfirm
                    :title="`Are you sure?`"
                    confirmButtonText="Confirm"
                    cancelButtonText="Cancel"
                    icon="el-icon-info"
                    iconColor="red"
                    @onConfirm="cancel(scope.row.id)">
                  <el-button :disabled="loading" slot="reference" size="mini">Cancel</el-button>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
    </el-main>
    <el-dialog :visible.sync="showDifferenceDialog" width="30%">
      <template slot="title">
        <h4>Select amount to refund:</h4>
      </template>
      <div v-if="refundDifference">
        <el-radio v-model="useLogonAmount" :label="true">Logon RA: ${{ refundDifference.logon_amount }}</el-radio>
        <el-radio v-model="useLogonAmount" :label="false">This Refund: ${{ refundDifference.refund_amount }}</el-radio>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="process(refundDifference.id)">Process</el-button>
        <el-button type="primary" @click="showDifferenceDialog = false">Cancel</el-button>
      </span>
    </el-dialog>
  </el-container>
</template>

<script>
import axios, {RepositoryError} from "../../repositories";
import {notify} from "../../utils";
import {RepositoryFactory} from "../../repositories/RepositoryFactory";
import Decimal from 'decimal.js';

export default {
  name: "Refunds",
  data() {
    return {
      refundSearchForm: {
        order_id: this.$route.params.order_id ?? null,
        filter: this.$route.params.filter ?? {
          return: null
        },
      },
      loading: false,
      showDifferenceDialog: false,
      refundDifference: null,
      useLogonAmount: null,
      refunds: [],
      returnOptions: [
        {value: null, label: 'All'},
        {value: 1, label: 'Return to Warehouse'},
        {value: 0, label: 'No Return'}
      ]
    }
  },
  created() {
    this.fetchRefunds();
  },
  methods: {
    async fetchRefunds() {
      this.loading = true;
      try {
        const params = {
          order_id: this.refundSearchForm.order_id,
          filter: this.refundSearchForm.filter
        }
        params.filter = _.omitBy(params.filter, _.isNull);
        const response = await axios.get('/refunds', { params: _.omitBy(params, _.isEmpty) });
        this.refunds = response.data.data;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      } finally {
        this.loading = false;
      }
    },
    warnDifference(refund) {
      this.refundDifference = refund;
      this.showDifferenceDialog = true;
    },
    process(id) {
      this.loading = true;
      this.showDifferenceDialog = false;
      const ShopifyOrderRefundRepository = RepositoryFactory.get('acadia/shopify/orders/refund');

      const index = _.findIndex(this.refunds, (ref) => ref.id === id);
      const refund = this.refunds[index];
      if(this.useLogonAmount) {
        const leftToRefund = new Decimal(refund.logon_amount);
        for(let i in refund.payload.transactions) {
          const trx = refund.payload.transactions[i];
          if (leftToRefund.toNumber() <= 0) {
            break;
          }
          // If the refund amount greater that the maximum refundable for the gateway
          // then we will max it out and distribute the rest amongst the remaining
          // gateways
          if (trx.hasOwnProperty('maximum_refundable') && leftToRefund > trx.maximum_refundable) {
            trx.amount = trx.maximum_refundable;
            leftToRefund.sub(trx.maximum_refundable);
          } else {
            trx.amount = leftToRefund.toFixed(2);
            break;
          }
          refund.payload.transactions[i] = trx;
        }
      }
      try {
        refund.payload.id = refund.id;
        ShopifyOrderRefundRepository.createRefund(refund.region, refund.order_id, refund.payload)
            .then(() => {
              notify('Refunded successfully', 'success');
              this.refunds.splice(index, 1);
            }, (error) => {
              notify(error, 'error');
            })
            .finally(() => {
              this.loading = false;
              this.refundDifference = null;
              this.useLogonAmount = false;
            });
      } catch (error) {
        notify(error.message, 'error');
        this.loading = false;
        this.refundDifference = null;
        this.useLogonAmount = false;
      }
    },
    cancel(id) {
      this.loading = true;
      const index = _.findIndex(this.refunds, (ref) => ref.id === id);
      const refund = this.refunds[index];
      axios.delete(`/refunds/${refund.id}`).then(() => {
        notify('Refund cancelled', 'success');
        this.refunds.splice(index, 1);
      }, (error) => {
        notify(error.message, 'error');
      }).finally(() => {
        this.loading = false;
      });

    }
  }
}
</script>